// Cart suggestions (sections/cart-recommendations.liquid), on the cart page and in the cart drawer.
// For the newest cart lines it shows the cached AI picks first (custom.cross_sells), then Shopify's
// related products, hides what is already in the cart or sold out, and refreshes when the cart changes.
// In the drawer it waits until the drawer is visible, so closed drawers cost no requests.
if (!customElements.get("cart-recommendations")) {
  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      this.onItemAdd = () => this.afterItemAdd();
      this.addEventListener("click", (event) => {
        const button = event.target.closest("button.cart-reco-add");
        if (button) this.add(button);
        const arrow = event.target.closest("button.cart-reco-arrow");
        if (arrow) this.slide(Number(arrow.dataset.dir));
      });
      this.querySelector(".cart-reco-list")?.addEventListener("scroll", () => this.updateNav(), { passive: true });
      document.addEventListener("cart:item-add", this.onItemAdd);
      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.load());
      }
      this.drawer = this.closest("mini-cart");
      if (this.drawer && "IntersectionObserver" in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        });
        this.observer.observe(this.parentElement || this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      document.removeEventListener("cart:item-add", this.onItemAdd);
      this.unsubscribe?.();
      this.observer?.disconnect();
    }

    // Suggestion rows for one cart line: its AI picks and Shopify's related picks, in that order.
    async fetchLine(productId) {
      const params = new URLSearchParams({
        section_id: this.dataset.sectionId,
        product_id: productId,
        limit: this.dataset.limit || "10",
        intent: "related",
      });
      const html = await fetch(`${this.dataset.url}?${params}`).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, "text/html");
      const rows = [...doc.querySelectorAll("[data-reco-product-id]")];
      return {
        ai: rows.filter((li) => li.dataset.recoTier === "ai"),
        related: rows.filter((li) => li.dataset.recoTier !== "ai"),
      };
    }

    async load() {
      const run = (this.run = (this.run || 0) + 1);
      try {
        const cart = await fetch(`${window.routes.cart_url}.js`).then((r) => r.json());
        if (!cart.items.length) return this.hide();

        const inCart = new Set(cart.items.map((item) => item.product_id));
        // Newest lines first (Shopify lists the latest addition first), at most three of them.
        const sources = [...new Set(cart.items.map((item) => item.product_id))].slice(0, 3);
        const lines = await Promise.all(sources.map((id) => this.fetchLine(id).catch(() => null)));
        if (run !== this.run) return;

        // AI picks of every line, taken in turns, then Shopify's related picks.
        const ordered = [];
        const aiLists = lines.filter(Boolean).map((line) => line.ai);
        for (let i = 0; aiLists.some((list) => i < list.length); i++) {
          aiLists.forEach((list) => list[i] && ordered.push(list[i]));
        }
        lines.filter(Boolean).forEach((line) => ordered.push(...line.related));

        const seen = new Set();
        const picks = ordered
          .filter((li) => li.dataset.recoAvailable === "true")
          .filter((li) => !inCart.has(Number(li.dataset.recoProductId)))
          .filter((li) => !seen.has(li.dataset.recoProductId) && seen.add(li.dataset.recoProductId))
          .slice(0, Number(this.dataset.show) || 6);
        if (!picks.length) return this.hide();

        const list = this.querySelector(".cart-reco-list");
        list.replaceChildren(...picks.map((li) => document.importNode(li, true)));
        list.scrollLeft = 0;
        this.hidden = false;
        this.updateNav();
      } catch (error) {
        console.error(error);
        this.hide();
      }
    }

    // Same supplier stock check as the theme's direct adds (assets/supplier-availability.js).
    async add(button) {
      if (button.getAttribute("aria-busy") === "true") return;
      button.setAttribute("aria-busy", "true");
      try {
        const item = { tags: button.dataset.tags, variant: button.dataset.variantId, quantity: 1 };
        const container = button.closest(".cart-reco-item");
        const allowed = window.SupplierAvailability
          ? await window.SupplierAvailability.checkItems([item], container)
          : true;
        if (!allowed) return;
        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: Number(item.variant), quantity: 1 }] }),
        });
        if (!response.ok) throw new Error(`cart add ${response.status}`);
        await this.afterItemAdd();
      } catch (error) {
        console.error(error);
      } finally {
        button.removeAttribute("aria-busy");
      }
    }

    hide() {
      this.hidden = true;
    }

    // Arrows (desktop): one page of 3 at a time; hidden when everything fits, disabled at the ends.
    slide(direction) {
      const list = this.querySelector(".cart-reco-list");
      list.scrollBy({ left: direction * list.clientWidth, behavior: "smooth" });
    }

    updateNav() {
      const list = this.querySelector(".cart-reco-list");
      const nav = this.querySelector(".cart-reco-nav");
      if (!list || !nav) return;
      const max = list.scrollWidth - list.clientWidth;
      nav.hidden = max <= 2;
      const [prev, next] = nav.querySelectorAll(".cart-reco-arrow");
      prev.disabled = list.scrollLeft <= 2;
      next.disabled = list.scrollLeft >= max - 2;
    }

    // A suggestion added: refresh the cart sections, then the picks.
    async afterItemAdd() {
      // In the drawer: re-render the drawer, which rebuilds this element with fresh picks.
      if (this.drawer?.updateSectionContents) {
        await this.drawer.updateSectionContents();
        return;
      }
      const cartItems = document.querySelector("cart-items");
      if (cartItems?.updateSectionContents) {
        if (cartItems.classList.contains("is-empty")) {
          location.reload();
          return;
        }
        await cartItems.updateSectionContents();
      }
      this.load();
    }
  }

  customElements.define("cart-recommendations", CartRecommendations);
}
